import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion, useScroll, useTransform } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { 
  ClockIcon, 
  ChartBarIcon, 
  ShieldCheckIcon,
  CurrencyDollarIcon
} from '@heroicons/react/24/outline';

const Home = () => {
  const { t, i18n } = useTranslation();
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 500], [0, -50]);
  const opacity = useTransform(scrollY, [0, 500], [1, 0]);

  // Define features data with fallback to ensure it's always an array
  const features = t('home.features.items', { returnObjects: true }) || [
    {
      title: t('home.features.items.0.title', 'Professional Market Making'),
      description: t('home.features.items.0.description', 'Providing professional market making services for exchanges and projects to enhance market liquidity')
    },
    {
      title: t('home.features.items.1.title', 'Liquidity Management'),
      description: t('home.features.items.1.description', 'Intelligent liquidity management solutions optimizing capital utilization efficiency')
    },
    {
      title: t('home.features.items.2.title', 'Risk Control'),
      description: t('home.features.items.2.description', 'Comprehensive risk control system ensuring fund security')
    }
  ];
  
  // Define partners data 
  const partners = [
    { 
      name: 'Gate.io', 
      description: t('home.partners.items.gate', 'Leading global cryptocurrency exchange')
    },
    { 
      name: 'Bitget', 
      description: t('home.partners.items.bitget', 'Innovative digital asset trading platform')
    },
    { 
      name: 'HTX', 
      description: t('home.partners.items.htx', 'Professional digital asset trading platform')
    },
    { 
      name: 'MEXC', 
      description: t('home.partners.items.mexc', 'Leading cryptocurrency trading platform')
    }
  ];

  return (
    <>
      <Helmet>
        <title>{t('home.meta.title', '9527.fi - Leading Crypto Market Maker')}</title>
        <meta name="description" content={t('home.meta.description', '9527.fi is a leading crypto market maker providing stable and reliable liquidity solutions for exchanges and projects.')} />
      </Helmet>

      {/* Hero Section */}
      <section className="relative bg-gradient-to-r from-primary to-secondary text-white min-h-[45vh] flex items-center">
        <motion.div 
          style={{ y, opacity }}
          className="absolute inset-0 bg-black/20"
        />
        <div className="container mx-auto px-4 py-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl mx-auto text-center"
          >
            <motion.h1 
              className="text-4xl md:text-5xl font-bold mb-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              {t('home.hero.title', 'Empowering Markets with Liquidity, Connecting Future with Value')}
            </motion.h1>
            <motion.p 
              className="text-xl mb-8 text-gray-200"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              {t('home.hero.subtitle', 'Leading crypto market maker providing stable and reliable liquidity solutions for exchanges and projects.')}
            </motion.p>
            <motion.div 
              className="flex flex-col sm:flex-row gap-4 justify-center"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
            >
              <Link 
                to="/contact" 
                className="btn btn-primary transform hover:scale-105 transition-transform"
              >
                {t('home.hero.contactUs', 'Contact Us')}
              </Link>
              <Link 
                to="/services" 
                className="btn bg-white text-primary hover:bg-gray-100 transform hover:scale-105 transition-transform"
              >
                {t('home.hero.learnMore', 'Learn More')}
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <motion.h2 
            className="section-title"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            {t('home.features.title', 'Core Advantages')}
          </motion.h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.2 }}
                className="bg-white p-6 rounded-lg shadow-lg transform hover:scale-105 transition-all duration-300"
              >
                <motion.img
                  src={`https://placehold.co/600x400/${index === 0 ? '3B82F6' : index === 1 ? '1E40AF' : '1E293B'}/ffffff?text=${index === 0 ? 'Market_Making' : index === 1 ? 'Liquidity' : 'Risk_Control'}`}
                  alt={feature.title}
                  className="h-40 w-full object-cover rounded-lg mb-4"
                  whileHover={{ scale: 1.05 }}
                  transition={{ duration: 0.3 }}
                />
                <h3 className="text-xl font-semibold mb-2">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Partners Section */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <motion.h2 
            className="section-title"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            {t('home.partners.title', 'Our Partners')}
          </motion.h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {partners.map((partner, index) => (
              <motion.div
                key={partner.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white p-6 rounded-lg shadow-lg text-center transform hover:scale-105 transition-all duration-300"
              >
                <motion.img
                  src={`https://placehold.co/200x80/${index === 0 ? '1E40AF' : index === 1 ? '1E293B' : index === 2 ? '3B82F6' : '1E40AF'}/ffffff?text=${partner.name}`}
                  alt={partner.name}
                  className="h-12 w-auto mx-auto mb-4"
                  whileHover={{ scale: 1.1 }}
                  transition={{ duration: 0.3 }}
                />
                <h3 className="text-lg font-semibold mb-2">{partner.name}</h3>
                <p className="text-sm text-gray-600">{partner.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-primary text-white">
        <div className="container mx-auto px-4 text-center">
          <motion.h2 
            className="text-3xl font-bold mb-6"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            {t('home.cta.title', 'Ready to Get Started?')}
          </motion.h2>
          <motion.p 
            className="text-xl mb-8 text-gray-200"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            {t('home.cta.subtitle', 'Contact us for professional liquidity solutions')}
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.4 }}
          >
            <motion.a
              href="mailto:stanley@9527.fi"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="btn bg-white text-primary hover:bg-gray-100 inline-block"
            >
              {t('home.cta.getQuote', 'Get Quote')}
            </motion.a>
          </motion.div>
        </div>
      </section>
    </>
  );
};

export default Home; 